import { useEffect } from "react";

type ContentType = {
  heading?: string;
  text?: string;
  list?: ListType[];
};

type ListType = {
  subheading: string;
  text: string;
};

const privacy: ContentType[] = [
  {
    heading: "",
    text: 'TechQilla ("Company," "we," "our," or "us") values your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, share, and protect your information when you use our website, services, and interact with us.',
  },
  {
    heading: "1. Information We Collect",
    text: "We collect personal and non-personal information to provide and improve our services. The types of data we collect include:",
    list: [
      {
        subheading: "Personal Information:",
        text: "Name, email address, phone number, company details, and billing information when you sign up for our services or contact us. ",
      },
      {
        subheading: "Usage Data:",
        text: "IP addresses, browser type, device information, and website usage statistics collected through cookies and analytics tools.",
      },
      {
        subheading: "Communication Data:",
        text: "Emails, messages, and other interactions with our team.",
      },
      {
        subheading: "Marketing Preferences:",
        text: "Data related to your engagement with our newsletters, ads, and promotional content.",
      },
    ],
  },
  {
    heading: "2. How We Use Your Information",
    text: "We use the collected information for the following purposes:",
    list: [
      {
        subheading: "",
        text: "To provide, manage, and improve our services.",
      },
      {
        subheading: "",
        text: "To personalize user experiences and enhance customer support.",
      },
      {
        subheading: "",
        text: "To process transactions and fulfill contractual obligations.",
      },
      {
        subheading: "",
        text: "To send important service updates, promotional emails, and marketing communications.",
      },
      {
        subheading: "",
        text: "To comply with legal obligations and protect against fraud or security threats.",
      },
    ],
  },
  {
    heading: "3. How We Share Your Information",
    text: "We do not sell or rent personal data. However, we may share your information with:",
    list: [
      {
        subheading: "Service Providers:",
        text: "Third-party partners who assist in payment processing, analytics, and service delivery.",
      },
      {
        subheading: "Legal Authorities:",
        text: "If required by law, regulation, or legal proceedings.",
      },
      {
        subheading: "Business Transfers:",
        text: "In case of a merger, acquisition, or asset sale, your information may be transferred to the new entity.",
      },
    ],
  },
  {
    heading: "4. Data Protection & Security",
    text: "We implement industry-standard security measures to protect your data from unauthorized access, loss, or misuse. While we take reasonable precautions, no online transmission is 100% secure. Users are encouraged to take their own security precautions when sharing personal data online.",
  },
  {
    heading: "5. Cookies & Tracking Technologies",
    text: "We use cookies and similar tracking technologies to improve user experience and analyze website traffic. You can manage cookie preferences through your browser settings.",
  },
  {
    heading: "6. Your Rights & Choices",
    text: "Depending on GDPR and CCPA, you may have the following rights regarding your personal data:",
    list: [
      {
        subheading: "Access & Correction:",
        text: "Request access to or corrections of your personal data.",
      },
      {
        subheading: "Deletion:",
        text: "Request deletion of your data under certain circumstances.",
      },
      {
        subheading: "Opt-Out:",
        text: "Unsubscribe from marketing communications at any time.",
      },
      {
        subheading: "Data Portability:",
        text: "Request a copy of your data in a machine-readable format.",
      },
    ],
  },
  {
    text: "To exercise these rights, contact us at <a href='mailto:hello@techqilla.com' className = 'font-semibold'>hello@techqilla.com.</a>",
  },
  {
    heading: "7. Third-Party Links & Services",
    text: "Our website may contain links to third-party websites. We are not responsible for the privacy practices of external sites and encourage users to review their policies before providing any personal data.",
  },
  {
    heading: "8. Data Retention",
    text: "We retain personal data for as long as necessary to fulfill the purposes outlined in this Privacy Policy, comply with legal obligations, and resolve disputes.  ",
  },
  {
    heading: "9. International Data Transfers",
    text: "If you are accessing our services from outside the United States or the United Kingdom, your information may be transferred to and processed in countries where data protection laws may differ from those in your jurisdiction.",
  },
  {
    heading: "10. Changes to This Privacy Policy",
    text: "We may update this Privacy Policy periodically to reflect changes in our practices. Any updates will be posted on this page with a revised effective date.",
  },
  {
    heading: "11. Contact Information",
    text: "If you have questions or concerns about this Privacy Policy, please contact us at:",
  },
  {
    text: "<a href='mailto:hello@techqilla.com' className = 'font-semibold'>Email: <span className='font-normal'>hello@techqilla.com<span></a>",
  },
  {
    text: "<a href='https://techqilla.com'' className = 'font-semibold'>Website: <span className = 'font-normal'>https://www.techqilla.com/</span></a>",
  },
  {
    text: "By using TechQilla’s services, you acknowledge and agree to this Privacy Policy.",
  },
];

const PrivacyPolicy = () => {
  useEffect(() => {
    // Set Page Title
    document.title = "Privacy Policy | TechQilla";

    // --- Description ---
    let metaDescription = document.querySelector<HTMLMetaElement>(
      "meta[name='description']",
    );
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute(
      "content",
      "Read TechQilla’s privacy policy to understand how we collect, use, and protect your personal information.",
    );

    // --- Keywords ---
    let metaKeywords = document.querySelector<HTMLMetaElement>(
      "meta[name='keywords']",
    );
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.name = "keywords";
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute("content", "Privacy Policy");
    // --- Canonical ---
    let linkTag = document.querySelector<HTMLLinkElement>(
      "link[rel='canonical']",
    );
    if (!linkTag) {
      linkTag = document.createElement("link");
      linkTag.rel = "canonical";
      document.head.appendChild(linkTag);
    }
    linkTag.setAttribute(
      "href",
      "https://www.techqilla.com/privacy-policy",
    );
  }, []);

  return (
    <div className="max-w-screen-2xl mx-auto px-5 sm:px-20 py-8 sm:py-28">
      <div className="py-4 sm:py-6 px-4 sm:px-6 lg:px-8 font-montserrat">
        {/* <Markdown rehypePlugins={[rehypeRaw]}>
          {termContext?.privacyContent.privacyPolicy}
        </Markdown> */}
        <h1 className="font-montserrat font-bold text-5xl mb-8">
          Privacy Policy
        </h1>
        {privacy.map((term) => (
          <div className="">
            {term.heading && (
              <h3 className="font-bold font-montserrat text-2xl mb-2 mt-2">
                {term.heading}
              </h3>
            )}
            <div>
              {term.text && (
                <p
                  className="mb-2"
                  dangerouslySetInnerHTML={{
                    __html: term.text.replaceAll("className", "class"),
                  }}
                ></p>
              )}
              {term.list && (
                <ul className="list-disc ml-8">
                  {term.list.map((li) => (
                    <li>
                      {li.subheading} <span>{li.text}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PrivacyPolicy;
